import {Device} from '@fantasmagames/core/src/device/Device';
import {CoreDI} from '@fantasmagames/core/src/di/CoreDI';
import {VisibilityManager} from '@fantasmagames/core/src/screen/VisibilityManager';
import {Tween} from '@fantasmagames/core/src/tween/Tween';
import {Timer} from '@fantasmagames/core/src/update/Timer';
import {UpdateManager} from '@fantasmagames/core/src/update/UpdateManager';
import DI from '@fantasmagames/slot/src/common/inversify/constants/DI';
import {Logic} from '@fantasmagames/slot/src/logic/Logic';
import {SoundManager} from '@fantasmagames/slot/src/sound/SoundManager';
import {SoundMusicVoiceOptionSwitch, SoundsDuckingConfig} from '@fantasmagames/slot/src/sound/SoundsDuckingConfig';
import {inject, injectable} from 'inversify';
import Sound = PIXI.sound.Sound;
import IMediaInstance = PIXI.sound.IMediaInstance;

export interface MobileSoundsMap {
	[id: string]: IMediaInstance;
}

export interface DesktopSoundsMap {
	[id: string]: IMediaInstance[];
}

export interface FadingsMap {
	[id: string]: Tween;
}

@injectable()
export class SoundPlayer {
	@inject(DI.SoundManager)
	protected _soundManager: SoundManager;

	@inject(CoreDI.UpdateManager)
	protected _updateManager: UpdateManager;

	@inject(CoreDI.Device)
	protected _device: Device;

	@inject(CoreDI.VisibilityManager)
	protected _visibilityManager: VisibilityManager;

	protected _soundsDuckingConfig: SoundsDuckingConfig;

	protected _isMusicPlaying: boolean;
	protected _currentMusicID: string = '';
	protected _currentMusicDuckingTween: Tween;
	protected _currentEffectsDuckingTween: Tween;
	protected _currentVoicesDuckingTween: Tween;
	protected _currentFadingMusicTween: Tween;
	protected _currentNewMusicTween: Tween;

	protected _isSoundEnabled: boolean = true;

	protected _isMobile: boolean;

	protected _mobileSoundsPlaying: MobileSoundsMap = {};

	protected _gameSoundsStatusChangedCallback: Function;
	protected _backgroundMusicStatusChangedCallback: Function;
	protected _allSoundsStatusChangedCallback: Function;

	protected _currentFadings: FadingsMap = {};

	constructor() {
		this.setupSoundsDuckingConfig();
		if (!__PRODUCTION__) {
			(window as any).soundPlayer = this;
		}
	}

	public init(): void {
		this._isMobile = this._device.isMobile;

		this._visibilityManager.onVisibleCallback = this.onPageShow.bind(this);
		this._visibilityManager.onInvisibleCallback = this.onPageHide.bind(this);
	}

	public enableAllSound(): void {
		PIXI.sound.unmuteAll();
		this._isSoundEnabled = true;

		if (this._allSoundsStatusChangedCallback) {
			this._allSoundsStatusChangedCallback(true);
		}
	}

	public disableAllSound(): void {
		PIXI.sound.muteAll();
		this._isSoundEnabled = false;

		if (this._allSoundsStatusChangedCallback) {
			this._allSoundsStatusChangedCallback(false);
		}
	}

	public enableMusic(): void {
		this._soundManager.unmuteAllMusic();

		if (this._backgroundMusicStatusChangedCallback) {
			this._backgroundMusicStatusChangedCallback(true);
		}
	}

	public disableMusic(): void {
		this._soundManager.muteAllMusic();

		if (this._backgroundMusicStatusChangedCallback) {
			this._backgroundMusicStatusChangedCallback(false);
		}
	}

	public enableVoice(): void {
		this._soundManager.unmuteAllVoice();
	}

	public disableVoice(): void {
		this._soundManager.muteAllVoice();
	}

	public enableSounds(): void {
		this._soundManager.unmuteAllSounds();

		if (this._gameSoundsStatusChangedCallback) {
			this._gameSoundsStatusChangedCallback(true);
		}
	}

	public disableSounds(): void {
		this._soundManager.muteAllSounds();

		if (this._gameSoundsStatusChangedCallback) {
			this._gameSoundsStatusChangedCallback(false);
		}
	}

	public play(soundID: string, loop?: boolean, chainIntoId?: string, loopChain?: boolean): void {
		this.removeFromFadings(soundID);

		if (this._isMobile) {
			this.playMobileSound(soundID, loop, chainIntoId, loopChain);
		} else {
			this.playDesktopSound(soundID, loop, chainIntoId, loopChain);
		}
	}

	public playVoice(voiceID: string): void {
		if (this._isMobile) {
			this.playMobileVoice(voiceID);
		} else {
			this.playDesktopVoice(voiceID);
		}
	}

	public stop(soundID: string): void {
		if (this._isMobile) {
			this.stopMobileSound(soundID);
		} else {
			this.stopDesktopSound(soundID);
		}
	}

	public stopFadeSound(soundID: string, fadeTime: number, callback: Function): void {
		let sfx: Sound | IMediaInstance = this._soundManager.getEffect(soundID);
		if (!sfx && this._isMobile) {
			sfx = this._mobileSoundsPlaying[soundID];
		}
		if (sfx) {
			if (!sfx.loop && fadeTime > this.getSoundLength(soundID) * 1000) {
				throw new Error(`Cannot run fade longer than sound length.`);
			}

			this.removeFromFadings(soundID);
			const fadeTween: Tween = new Tween(sfx)
				.to({volume: 0.0}, fadeTime)
				.start()
				.onComplete(() => {
					// If it's not 0 the sound has probably been restarted/played from some other place and should not be stopped by this.
					if (sfx.volume === 0) {
						this.stop(soundID);
					}
					if (callback) {
						callback();
					}
					this.removeFromFadings(soundID);
				});
			this._currentFadings[soundID] = fadeTween;

			this._updateManager.add(fadeTween);
		}
	}

	protected removeFromFadings(soundID: string): void {
		const tween: Tween = this._currentFadings[soundID];
		if (tween && tween.isPlaying) {
			tween.stop(true);
			this._currentFadings[soundID] = null;
		}
	}

	public playMusic(musicID: string): void {
		this.stopMusic();
		if (this._soundManager.hasMusic(musicID)) {
			const music: Sound = this._soundManager.getMusic(musicID);
			const volume: number = this._soundManager.getSoundVolume(musicID);
			if (!music.isPlaying) {
				music.play({loop: true});
			} else if (volume > music.volume) {
				music.stop();
				music.play({loop: true});
			}

			music.volume = volume;
			this._isMusicPlaying = true;
			this._currentMusicID = musicID;
		}
	}

	public fadeCurrentMusicTo(musicID: string, transitionDuration: number = 4000): void {
		if (this._soundManager.hasMusic(musicID) && musicID !== this._currentMusicID) {
			const curMusic: Sound = this._soundManager.getMusic(this._currentMusicID);
			this.killCurrentMusicFadingOutTween();
			this.killCurrentNewMusicTween();

			if (curMusic && curMusic.isPlaying) {
				const currentMusicTween: Tween = new Tween(curMusic).to({volume: 0.0}, transitionDuration).start();
				this._updateManager.add(currentMusicTween);
				this._currentFadingMusicTween = currentMusicTween;
			}

			const newMusic: Sound = this._soundManager.getMusic(musicID);
			const volume: number = this._soundManager.getSoundVolume(musicID);
			if (!newMusic.isPlaying) {
				if (newMusic.instances[0]) {
					newMusic.instances[0].progress = 0;
				}
				newMusic.play({loop: true});
			}

			this._isMusicPlaying = true;
			this._currentMusicID = musicID;
			newMusic.volume = 0;
			const newMusicTween: Tween = new Tween(newMusic).to({volume}, transitionDuration).start();
			this._updateManager.add(newMusicTween);
			this._currentNewMusicTween = newMusicTween;
		} else {
			// Case where it tries to transition from itself.
			if (this._soundManager.hasMusic(musicID) && musicID === this._currentMusicID) {
				this.killCurrentMusicFadingOutTween();
				this.killCurrentNewMusicTween();

				const newMusic: Sound = this._soundManager.getMusic(musicID);
				const volume: number = this._soundManager.getSoundVolume(musicID);

				if (volume === 1) {
					return;
				}

				if (!newMusic.isPlaying) {
					newMusic.play({loop: true});
				}

				this._isMusicPlaying = true;
				this._currentMusicID = musicID;
				newMusic.volume = volume;
				const newMusicTween: Tween = new Tween(newMusic).to({volume}, transitionDuration).start();
				this._updateManager.add(newMusicTween);
				this._currentNewMusicTween = newMusicTween;
			}
		}
	}

	public fadeCurrentMusic(transitionDuration: number = 4000): void {
		const curMusic: Sound = this._soundManager.getMusic(this._currentMusicID);
		if (curMusic && curMusic.isPlaying) {
			this.killCurrentEffectsDuckingTween();
			this.killCurrentNewMusicTween();

			const currentMusicTween: Tween = new Tween(curMusic).to({volume: 0.0}, transitionDuration).start();
			this._updateManager.add(currentMusicTween);
			this._currentFadingMusicTween = currentMusicTween;
		}

		this._isMusicPlaying = false;
		this._currentMusicID = null;
	}

	public stopMusic(): void {
		if (this._isMusicPlaying) {
			this._soundManager.getMusic(this._currentMusicID).stop();
			this._isMusicPlaying = false;
		}
	}

	public isMusicPlaying(): boolean {
		return this._isMusicPlaying;
	}

	public duckMusic(duckingTime: number = 500): void {
		this.killCurrentMusicDuckingTween();

		const music: Sound = this._soundManager.getMusic(this._currentMusicID);
		if (music) {
			this._currentMusicDuckingTween = new Tween(music).to({volume: 0.0}, duckingTime).start();
			this._updateManager.add(this._currentMusicDuckingTween);
		}
	}

	public unduckMusic(unduckingTime: number = 500): void {
		this.killCurrentMusicDuckingTween();

		const music: Sound = this._soundManager.getMusic(this._currentMusicID);
		if (music) {
			this._currentMusicDuckingTween = new Tween(music).to({volume: 1.0}, unduckingTime).start();
			this._updateManager.add(this._currentMusicDuckingTween);
		}
	}

	public duckEffects(duckingTime: number = 500): void {
		this.killCurrentEffectsDuckingTween();

		const effects = this._soundManager.getAllPlayingEffects();
		if (effects.length) {
			this._currentEffectsDuckingTween = this.tweenAttributeForListOfSoundInstances('volume', effects, 1, 0, duckingTime);
		}
	}

	public unduckEffects(unduckingTime: number = 300): void {
		this.killCurrentEffectsDuckingTween();

		const effects = this._soundManager.getAllPlayingEffects();
		if (effects.length) {
			this._currentEffectsDuckingTween = this.tweenAttributeForListOfSoundInstances('volume', effects, 0, 1, unduckingTime);
		}
	}

	public duckVoices(duckingTime: number = 500): void {
		this.killCurrentVoicesDuckingTween();

		const voices = this._soundManager.getAllPlayingVoices();
		if (voices.length) {
			this._currentVoicesDuckingTween = this.tweenAttributeForListOfSoundInstances('volume', voices, 1, 0, duckingTime);
		}
	}

	public unduckVoices(unduckingTime: number = 300): void {
		this.killCurrentVoicesDuckingTween();

		const voices = this._soundManager.getAllPlayingVoices();
		if (voices.length) {
			this._currentVoicesDuckingTween = this.tweenAttributeForListOfSoundInstances('volume', voices, 0, 1, unduckingTime);
		}
	}

	public restoreGameMusic(logic: Logic): void {
		throw new Error('SoundPlayer::restoreGameMusic must be overridden in order to be used');
	}

	public getSoundLength(id: string): number {
		return this._soundManager.getSoundLength(id);
	}

	public getMusicLength(id: string): number {
		return this._soundManager.getMusicLength(id);
	}

	public getVoiceLength(id: string): number {
		return this._soundManager.getVoiceLength(id);
	}

	protected tweenAttributeForListOfSoundInstances(
		attribute: string = 'volume',
		list: IMediaInstance[],
		start: number = 0,
		goal: number = 1,
		duration: number = 500,
	): Tween {
		const tweenObject = {tweenValue: start};

		const tween = new Tween(tweenObject)
			.to({tweenValue: goal}, duration)
			.onUpdate(() => {
				for (let i = list.length - 1; i >= 0; i--) {
					const mediaInstance = list[i];
					// This check is added because media is null when the sound is finished.
					if (mediaInstance) {
						if (this.isMediaInstanceFinishedPlaying(mediaInstance)) {
							list.splice(i, 1);
							continue;
						} else {
							mediaInstance[attribute] = tweenObject.tweenValue;
						}
					}
				}
			})
			.start();

		this._updateManager.add(tween);

		return tween;
	}

	protected isMediaInstanceFinishedPlaying(mediaInstance: IMediaInstance): boolean {
		return (mediaInstance as any)._media === null;
	}

	protected setupSoundsDuckingConfig(): void {
		this._soundsDuckingConfig = new SoundsDuckingConfig();
	}

	protected onPageHide(): void {
		if (this._isSoundEnabled) {
			this.disableAllSound();
		}
	}

	protected onPageShow(): void {
		if (!this._isSoundEnabled) {
			this.enableAllSound();
		}
	}

	protected playDesktopSound(soundID: string, loop?: boolean, chainIntoId?: string, loopChain?: boolean): void {
		const effect = this._soundManager.getEffect(soundID);
		const volume: number = this._soundManager.getSoundVolume(soundID);
		if (effect) {
			effect.volume = volume;
		}

		const chainedSound = this._soundManager.getEffect(chainIntoId);
		const chainedSoundVolume: number = this._soundManager.getSoundVolume(chainIntoId);
		if (chainedSound) {
			chainedSound.volume = chainedSoundVolume;
		}

		if (!effect) {
			if (!__PRODUCTION__) {
				console.warn(`There's no sound: '${soundID}'`);
			}

			return;
		}

		if (this._soundsDuckingConfig.hasDuckingTimeFor(soundID)) {
			this.duckSoundsForSoundId(soundID);

			effect.play({loop, complete: this.onDuckingSndCompleted.bind(this)});
		} else {
			if (chainIntoId) {
				effect.play({loop, complete: () => {
						chainedSound.play({loop: loopChain});
					}});

			} else {
				effect.play({loop});
			}
		}
	}

	protected duckSoundsForSoundId(soundID: string): void {
		const sounds: SoundMusicVoiceOptionSwitch = this._soundsDuckingConfig.getDuckingSoundsFor(soundID);
		const duckingTime: number = this._soundsDuckingConfig.getDuckingTimeFor(soundID);

		if (sounds.music && duckingTime) {
			this.duckMusic(duckingTime);
		}

		if (sounds.effects && duckingTime) {
			this.duckEffects(duckingTime);
		}

		if (sounds.voice && duckingTime) {
			this.duckVoices(duckingTime);
		}
	}

	protected unduckSoundsForSoundId(soundID: string): void {
		const sounds: SoundMusicVoiceOptionSwitch = this._soundsDuckingConfig.getDuckingSoundsFor(soundID);
		const unduckingTime: number = this._soundsDuckingConfig.getUnduckingTimeFor(soundID);

		if (sounds.music && unduckingTime) {
			this.duckMusic(unduckingTime);
		}

		if (sounds.effects && unduckingTime) {
			this.duckEffects(unduckingTime);
		}

		if (sounds.voice && unduckingTime) {
			this.duckVoices(unduckingTime);
		}
	}

	protected playMobileSound(soundID: string, loop: boolean = false, chainIntoId?: string, loopChain?: boolean): void {
		const sound = this._soundManager.getEffect('allSound');
		const volume: number = this._soundManager.getSoundVolume(soundID);

		if (sound && sound.sprites.hasOwnProperty(soundID)) {
			let currentSoundInstance: IMediaInstance;

			if (this._soundsDuckingConfig.hasDuckingTimeFor(soundID)) {
				this.duckSoundsForSoundId(soundID);
				currentSoundInstance = sound.play(
					{
						sprite: soundID,
						volume,
					},
					this.onDuckingSndCompleted.bind(this),
				) as IMediaInstance;
			} else {
				currentSoundInstance = sound.play({
					sprite: soundID,
					loop,
					volume,
					complete: () => {
						if (chainIntoId) {
							this.playMobileSound(chainIntoId, loopChain);
						}
					}}) as IMediaInstance;
			}

			if (currentSoundInstance) {
				this._mobileSoundsPlaying[soundID] = currentSoundInstance;
			}
		} else if (this._soundManager.getEffect(soundID)) {
			const sfx = this._soundManager.getEffect(soundID);
			sfx.volume = volume;

			if (this._soundsDuckingConfig.hasDuckingTimeFor(soundID)) {
				this.duckSoundsForSoundId(soundID);
				sfx.play({loop, complete: this.onDuckingSndCompleted.bind(this)});
			} else {
				sfx.play({loop, complete: () => {
						if (chainIntoId) {
							this.playMobileSound(chainIntoId, loopChain);
						}
					}});
			}

			sfx.loop = loop;
		}
	}

	protected playMobileVoice(voiceID: string): void {
		this.playMobileSound(voiceID);
	}

	protected playDesktopVoice(voiceID: string): void {
		const voice = this._soundManager.getVoice(voiceID);
		const volume: number = this._soundManager.getSoundVolume(voiceID);
		if (voice) {
			voice.volume = volume;
		}

		if (!voice) {
			if (!__PRODUCTION__) {
				console.warn(`There's no sound (Voice) called: '${voiceID}'`);
			}

			return;
		}

		if (this._soundsDuckingConfig.hasDuckingTimeFor(voiceID)) {
			this.duckSoundsForSoundId(voiceID);
			voice.play({loop: false, complete: this.onDuckingSndCompleted.bind(this)});
		} else {
			voice.play({loop: false});
		}
	}

	protected onDuckingSndCompleted(sound: Sound): void {
		this.unduckMusic();
	}

	protected stopDesktopSound(soundID: string): void {
		const sound = this._soundManager.getEffect(soundID);

		if (!sound) {
			if (!__PRODUCTION__) {
				console.warn(`There's no sound-effect called: '${soundID}'`);
			}

			return;
		}

		sound.stop();

		if (this._soundsDuckingConfig.hasDuckingTimeFor(soundID)) {
			this.unduckMusic();
		}
	}

	protected stopMobileSound(soundID: string): void {
		const sfx = this._soundManager.getEffect(soundID);
		if (sfx) {
			if (sfx.isPlaying) {
				sfx.stop();
			}
		} else {
			for (const key in this._mobileSoundsPlaying) {
				if (this._mobileSoundsPlaying.hasOwnProperty(key)) {
					const sound = this._mobileSoundsPlaying[key];
					if (key === soundID) {
						sound.stop();
					}
				}
			}
		}
	}

	protected killCurrentVoicesDuckingTween(): void {
		this.killTween(this._currentVoicesDuckingTween);
	}

	protected killCurrentEffectsDuckingTween(): void {
		this.killTween(this._currentEffectsDuckingTween);
	}

	protected killCurrentMusicDuckingTween(): void {
		this.killTween(this._currentMusicDuckingTween);
	}

	protected killCurrentMusicFadingOutTween(): void {
		this.killTween(this._currentFadingMusicTween, true);
	}
	protected killCurrentNewMusicTween(): void {
		this.killTween(this._currentNewMusicTween, true);
	}

	private killTween(tween: Tween, goToEnd: boolean = false): void {
		if (tween && tween.isPlaying) {
			tween.stop(goToEnd);
			this._updateManager.remove(tween);
		}
	}

	public set gameSoundsStatusChangedCallback(value: Function) {
		this._gameSoundsStatusChangedCallback = value;
	}

	public set backgroundMusicStatusChangedCallback(value: Function) {
		this._backgroundMusicStatusChangedCallback = value;
	}

	public set allSoundsStatusChangedCallback(value: Function) {
		this._allSoundsStatusChangedCallback = value;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/sound/SoundPlayer.ts